import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaSave, FaArrowLeft, FaPlus, FaTimes, FaYoutube, FaCamera } from 'react-icons/fa';
import {
  createUserExercise,
  updateUserExercise,
  getUserExercises,
  getUserWorkouts,
  updateUserWorkout,
  createUserWorkout,
  getUserPrograms,
  updateUserProgram,
  createUserProgram
} from '../../services/fitnessService';
import toast from 'react-hot-toast';
import ExerciseImageImport from './ExerciseImageImport';
import './ExerciseCreator.css';

const CATEGORIES = ['strength', 'cardio', 'flexibility', 'mobility', 'core', 'warmup', 'cooldown'];
const DIFFICULTIES = ['beginner', 'intermediate', 'advanced'];
const MEASUREMENT_TYPES = ['reps', 'time', 'distance'];

const getYouTubeThumbUrl = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
  return match ? `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg` : null;
};

function cleanBlockPayload(block) {
  if (!block) return null;
  return {
    name: block.name,
    description: block.description || '',
    rounds: parseInt(block.rounds, 10) || 1,
    restBetweenRounds: parseInt(block.restBetweenRounds, 10) || 60,
    exercises: (block.exercises || []).map((e, i) => ({
      exercise: e.exercise?._id || e.exercise,
      exerciseId: e.exerciseId || e.exercise?.exerciseId || '',
      order: i,
      sets: parseInt(e.sets, 10) || 3,
      reps: e.reps != null && e.reps !== '' ? parseInt(e.reps, 10) : undefined,
      duration: e.duration != null && e.duration !== '' ? parseInt(e.duration, 10) : undefined,
      restAfter: parseInt(e.restAfter, 10) || 45,
      notes: e.notes || '',
      isSuperset: !!e.isSuperset,
      supersetGroup: e.supersetGroup || undefined
    }))
  };
}

function workoutToUpdatePayload(workout) {
  return {
    name: workout.name,
    description: workout.description || '',
    goal: workout.goal || '',
    estimatedDuration: parseInt(workout.estimatedDuration, 10) || 30,
    difficulty: workout.difficulty || 'beginner',
    category: workout.category || 'strength',
    targetAreas: workout.targetAreas || [],
    warmup: workout.warmup ? cleanBlockPayload(workout.warmup) : undefined,
    blocks: (workout.blocks || []).map(cleanBlockPayload).filter(Boolean),
    cooldown: workout.cooldown ? cleanBlockPayload(workout.cooldown) : undefined,
    equipmentNeeded: workout.equipmentNeeded || [],
    tags: workout.tags || []
  };
}

function appendExerciseToWorkoutPayload(workout, exerciseDoc) {
  const payload = workoutToUpdatePayload(workout);
  if (!payload.blocks || payload.blocks.length === 0) {
    payload.blocks = [{
      name: 'Main Block',
      description: '',
      rounds: 1,
      restBetweenRounds: 60,
      exercises: []
    }];
  }
  const b = { ...payload.blocks[0], exercises: [...(payload.blocks[0].exercises || [])] };
  const order = b.exercises.length;
  b.exercises.push({
    exercise: exerciseDoc._id,
    exerciseId: exerciseDoc.exerciseId,
    order,
    sets: exerciseDoc.defaultSets || 3,
    reps: exerciseDoc.measurementType === 'reps' ? exerciseDoc.defaultReps : undefined,
    duration: exerciseDoc.measurementType === 'time' ? exerciseDoc.defaultDuration : undefined,
    restAfter: exerciseDoc.restBetweenSets || 45,
    notes: '',
    isSuperset: false
  });
  payload.blocks[0] = b;
  return payload;
}

function buildSingleExerciseWorkoutPayload(exerciseDoc) {
  return {
    name: `${exerciseDoc.name || 'Exercise'} — workout`,
    description: `Contains: ${exerciseDoc.name || 'exercise'}`,
    goal: '',
    estimatedDuration: 30,
    difficulty: exerciseDoc.difficulty || 'beginner',
    category: exerciseDoc.category || 'strength',
    targetAreas: exerciseDoc.targetMuscles || [],
    blocks: [{
      name: 'Main Block',
      description: '',
      rounds: 1,
      restBetweenRounds: 60,
      exercises: [{
        exercise: exerciseDoc._id,
        exerciseId: exerciseDoc.exerciseId,
        order: 0,
        sets: exerciseDoc.defaultSets || 3,
        reps: exerciseDoc.measurementType === 'reps' ? exerciseDoc.defaultReps : undefined,
        duration: exerciseDoc.measurementType === 'time' ? exerciseDoc.defaultDuration : undefined,
        restAfter: exerciseDoc.restBetweenSets || 45,
        notes: '',
        isSuperset: false
      }]
    }],
    equipmentNeeded: exerciseDoc.equipmentNames || [],
    tags: []
  };
}

export default function ExerciseCreator({ isCoach = false }) {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!editId);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('strength');
  const [difficulty, setDifficulty] = useState('beginner');
  const [measurementType, setMeasurementType] = useState('reps');

  const [defaultSets, setDefaultSets] = useState(3);
  const [defaultReps, setDefaultReps] = useState('');
  const [defaultDuration, setDefaultDuration] = useState('');
  const [restBetweenSets, setRestBetweenSets] = useState(45);

  const [targetMuscles, setTargetMuscles] = useState('');
  const [secondaryMuscles, setSecondaryMuscles] = useState('');
  const [equipmentNames, setEquipmentNames] = useState('');

  const [instructions, setInstructions] = useState([{ step: 1, text: '' }]);

  const [video, setVideo] = useState('');
  const [image, setImage] = useState('');

  const [tips, setTips] = useState('');
  const [safetyNotes, setSafetyNotes] = useState('');
  const [modEasier, setModEasier] = useState('');
  const [modHarder, setModHarder] = useState('');
  const [tags, setTags] = useState('');

  const [showPostSave, setShowPostSave] = useState(false);
  const [lastSavedExercise, setLastSavedExercise] = useState(null);

  const [showImageImport, setShowImageImport] = useState(false);
  const [extractedExercises, setExtractedExercises] = useState([]);
  const [extractedIndex, setExtractedIndex] = useState(0);
  const [decodedQrHint, setDecodedQrHint] = useState([]);

  const [postSaveStep, setPostSaveStep] = useState('main');
  const [placementLoading, setPlacementLoading] = useState(false);
  const [workoutsForPick, setWorkoutsForPick] = useState([]);
  const [programsForPick, setProgramsForPick] = useState([]);
  const [pickWorkoutId, setPickWorkoutId] = useState('');
  const [pickProgramId, setPickProgramId] = useState('');
  const [pickProgramDayIndex, setPickProgramDayIndex] = useState(0);

  useEffect(() => {
    if (!editId) return;
    const loadExercise = async () => {
      try {
        if (isCoach) {
          const { getCoachExercises } = await import('../../services/coachFitnessService');
          const data = await getCoachExercises();
          const exercises = data.exercises || data;
          const ex = exercises.find(e => e._id === editId);
          if (ex) populateForm(ex);
        } else {
          const exercises = await getUserExercises();
          const ex = exercises.find(e => e._id === editId);
          if (ex) populateForm(ex);
        }
      } catch (err) {
        toast.error('Failed to load exercise');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadExercise();
  }, [editId, isCoach]);

  const populateForm = useCallback((ex) => {
    setName(ex.name || '');
    setDescription(ex.description || '');
    setCategory(ex.category || 'strength');
    setDifficulty(ex.difficulty || 'beginner');
    setMeasurementType(ex.measurementType || 'reps');
    setDefaultSets(ex.defaultSets || 3);
    setDefaultReps(ex.defaultReps != null ? String(ex.defaultReps) : '');
    setDefaultDuration(ex.defaultDuration != null ? String(ex.defaultDuration) : '');
    setRestBetweenSets(ex.restBetweenSets || 45);
    setTargetMuscles((ex.targetMuscles || []).join(', '));
    setSecondaryMuscles((ex.secondaryMuscles || []).join(', '));
    setEquipmentNames((ex.equipmentNames || []).join(', '));
    setInstructions(ex.instructions?.length > 0 ? ex.instructions : [{ step: 1, text: '' }]);
    setVideo(ex.video || '');
    setImage(ex.image || '');
    setTips((ex.tips || []).join('\n'));
    setSafetyNotes((ex.safetyNotes || []).join('\n'));
    setModEasier(ex.modifications?.easier || '');
    setModHarder(ex.modifications?.harder || '');
    setTags((ex.tags || []).join(', '));
  }, []);

  useEffect(() => {
    if (!extractedExercises.length) return;
    const ex = extractedExercises[extractedIndex];
    if (ex) populateForm(ex);
  }, [extractedIndex, extractedExercises, populateForm]);

  const addInstruction = () => {
    setInstructions(prev => [...prev, { step: prev.length + 1, text: '' }]);
  };

  const removeInstruction = (index) => {
    setInstructions(prev => prev.filter((_, i) => i !== index).map((inst, i) => ({ ...inst, step: i + 1 })));
  };

  const updateInstruction = (index, text) => {
    setInstructions(prev => prev.map((inst, i) => (i === index ? { ...inst, text } : inst)));
  };

  const buildPayload = () => ({
    name,
    description,
    category,
    difficulty,
    measurementType,
    defaultSets: parseInt(defaultSets, 10) || 3,
    defaultReps: measurementType === 'reps' && defaultReps ? parseInt(defaultReps, 10) : undefined,
    defaultDuration: measurementType === 'time' && defaultDuration ? parseInt(defaultDuration, 10) : undefined,
    restBetweenSets: parseInt(restBetweenSets, 10) || 45,
    targetMuscles: targetMuscles.split(',').map(s => s.trim()).filter(Boolean),
    secondaryMuscles: secondaryMuscles.split(',').map(s => s.trim()).filter(Boolean),
    equipmentNames: equipmentNames.split(',').map(s => s.trim()).filter(Boolean),
    instructions: instructions.filter(i => i.text.trim()),
    video,
    image,
    tips: tips.split('\n').map(s => s.trim()).filter(Boolean),
    safetyNotes: safetyNotes.split('\n').map(s => s.trim()).filter(Boolean),
    modifications: { easier: modEasier, harder: modHarder },
    tags: tags.split(',').map(s => s.trim()).filter(Boolean)
  });

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Exercise name is required');
      return;
    }
    if (!description.trim()) {
      toast.error('Description is required');
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload();
      let saved = null;
      if (editId) {
        if (isCoach) {
          const { updateCoachExercise } = await import('../../services/coachFitnessService');
          saved = await updateCoachExercise(editId, payload);
        } else {
          saved = await updateUserExercise(editId, payload);
        }
        toast.success('Exercise updated!');
        setLastSavedExercise(null);
      } else {
        if (isCoach) {
          const { createCoachExercise } = await import('../../services/coachFitnessService');
          saved = await createCoachExercise(payload);
        } else {
          saved = await createUserExercise(payload);
        }
        toast.success('Exercise created!');
        setLastSavedExercise(saved);
      }
      setShowPostSave(true);
      setPostSaveStep('main');
    } catch (err) {
      toast.error(err.message || 'Failed to save exercise');
    } finally {
      setSaving(false);
    }
  };

  const openImport = () => setShowImageImport(true);

  const onImportSuccess = ({ exercises, decodedQrUrls }) => {
    setShowImageImport(false);
    if (!exercises?.length) {
      toast.error('No exercises found in those images');
      return;
    }
    setDecodedQrHint(decodedQrUrls || []);
    setExtractedIndex(0);
    setExtractedExercises(exercises);
    toast.success(`Loaded ${exercises.length} exercise(s) — review and save when ready`);
  };

  const thumbUrl = getYouTubeThumbUrl(video);

  const resetPlacementUi = () => {
    setPostSaveStep('main');
    setPickWorkoutId('');
    setPickProgramId('');
    setPickProgramDayIndex(0);
    setWorkoutsForPick([]);
    setProgramsForPick([]);
  };

  const handlePostSaveClose = () => {
    setShowPostSave(false);
    resetPlacementUi();
  };

  const loadWorkoutsForPlacement = async () => {
    setPlacementLoading(true);
    try {
      const list = await getUserWorkouts();
      setWorkoutsForPick(Array.isArray(list) ? list : []);
      setPostSaveStep('pick-workout');
      if (list?.length) setPickWorkoutId(list[0]._id);
    } catch (e) {
      toast.error(e.message || 'Failed to load workouts');
    } finally {
      setPlacementLoading(false);
    }
  };

  const confirmAddToWorkout = async () => {
    if (!lastSavedExercise || !pickWorkoutId) return;
    const workout = workoutsForPick.find(w => w._id === pickWorkoutId);
    if (!workout) return;
    setPlacementLoading(true);
    try {
      const payload = appendExerciseToWorkoutPayload(workout, lastSavedExercise);
      await updateUserWorkout(workout._id, payload);
      toast.success('Added to workout');
      handlePostSaveClose();
      navigate(`/fitness/workout/${workout.workoutId || workout._id}`);
    } catch (e) {
      toast.error(e.message || 'Failed to update workout');
    } finally {
      setPlacementLoading(false);
    }
  };

  const confirmCreateWorkout = async () => {
    if (!lastSavedExercise) return;
    setPlacementLoading(true);
    try {
      const payload = buildSingleExerciseWorkoutPayload(lastSavedExercise);
      const created = await createUserWorkout(payload);
      toast.success('Workout created');
      handlePostSaveClose();
      navigate(`/fitness/workout/${created.workoutId || created._id}`);
    } catch (e) {
      toast.error(e.message || 'Failed to create workout');
    } finally {
      setPlacementLoading(false);
    }
  };

  const loadProgramsForPlacement = async () => {
    setPlacementLoading(true);
    try {
      const list = await getUserPrograms();
      const programs = Array.isArray(list) ? list : list?.programs || [];
      setProgramsForPick(programs);
      setPostSaveStep('pick-program');
      if (programs.length) {
        setPickProgramId(programs[0]._id);
        setPickProgramDayIndex(0);
      }
    } catch (e) {
      toast.error(e.message || 'Failed to load programs');
    } finally {
      setPlacementLoading(false);
    }
  };

  const confirmAssignProgramDay = async () => {
    if (!lastSavedExercise || !pickProgramId) return;
    const program = programsForPick.find(p => p._id === pickProgramId);
    if (!program?.workouts?.length) {
      toast.error('This program has no day slots');
      return;
    }
    setPlacementLoading(true);
    try {
      const wPayload = buildSingleExerciseWorkoutPayload(lastSavedExercise);
      const newWorkout = await createUserWorkout(wPayload);

      const slots = program.workouts;
      const idx = Math.min(pickProgramDayIndex, slots.length - 1);
      const newWorkouts = slots.map((slot, i) => {
        if (i !== idx) {
          return {
            workout: slot.workout?._id || slot.workout,
            workoutId: slot.workoutId || '',
            dayNumber: slot.dayNumber,
            dayName: slot.dayName || `Day ${slot.dayNumber}`,
            isRestDay: !!slot.isRestDay,
            notes: slot.notes || ''
          };
        }
        return {
          workout: newWorkout._id,
          workoutId: newWorkout.workoutId,
          dayNumber: slot.dayNumber,
          dayName: slot.dayName || `Day ${slot.dayNumber}`,
          isRestDay: false,
          notes: slot.notes || ''
        };
      });

      await updateUserProgram(program._id, { workouts: newWorkouts });
      toast.success('Workout assigned to program day');
      handlePostSaveClose();
      navigate(`/fitness/program/${program.programId || program._id}`);
    } catch (e) {
      toast.error(e.message || 'Failed to update program');
    } finally {
      setPlacementLoading(false);
    }
  };

  const confirmCreateProgram = async () => {
    if (!lastSavedExercise) return;
    setPlacementLoading(true);
    try {
      const wPayload = buildSingleExerciseWorkoutPayload(lastSavedExercise);
      const newWorkout = await createUserWorkout(wPayload);
      const createdProgram = await createUserProgram({
        name: `${lastSavedExercise.name || 'PT'} program`,
        description: 'Created from imported exercise',
        goal: '',
        durationWeeks: 4,
        daysPerWeek: 1,
        sessionDuration: '30-45 minutes',
        difficulty: lastSavedExercise.difficulty || 'beginner',
        targetAudience: '',
        focus: lastSavedExercise.targetMuscles || [],
        workouts: [{
          workout: newWorkout._id,
          workoutId: newWorkout.workoutId,
          dayNumber: 1,
          dayName: 'Day 1',
          isRestDay: false,
          notes: ''
        }],
        guidelines: [],
        progressionNotes: [],
        equipmentNeeded: lastSavedExercise.equipmentNames || [],
        tags: []
      });
      toast.success('Program created');
      handlePostSaveClose();
      navigate(`/fitness/program/${createdProgram.programId || createdProgram._id}`);
    } catch (e) {
      toast.error(e.message || 'Failed to create program');
    } finally {
      setPlacementLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="exercise-creator">
        <div className="creator-loading">
          <div className="loading-spinner" />
          <p>Loading exercise...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="exercise-creator">
      {showImageImport && (
        <div className="ec-import-overlay">
          <div className="ec-import-modal">
            <ExerciseImageImport
              onExtractSuccess={onImportSuccess}
              onCancel={() => setShowImageImport(false)}
            />
          </div>
        </div>
      )}

      <div className="creator-container">
        <div className="creator-top-bar">
          <button type="button" className="back-btn" onClick={() => navigate(-1)}>
            <FaArrowLeft /> Back
          </button>
          <h1>{editId ? 'Edit Exercise' : 'Create Exercise'}</h1>
          <button type="button" className="save-btn" onClick={handleSave} disabled={saving}>
            <FaSave /> {saving ? 'Saving...' : 'Save Exercise'}
          </button>
        </div>

        {!editId && (
          <section className="creator-section ec-import-section">
            <h2>Import from photo</h2>
            <p className="ec-import-blurb">
              Snap or upload a PT / home-exercise handout. We scan QR demo links and fill fields with AI — always review before saving.
            </p>
            <button type="button" className="ec-import-open-btn" onClick={openImport}>
              <FaCamera /> Import from photos
            </button>
            {extractedExercises.length > 1 && (
              <div className="ec-extracted-picker">
                <label htmlFor="ec-ex-idx">Draft ({extractedExercises.length} exercises)</label>
                <select
                  id="ec-ex-idx"
                  value={extractedIndex}
                  onChange={e => setExtractedIndex(parseInt(e.target.value, 10))}
                >
                  {extractedExercises.map((ex, i) => (
                    <option key={i} value={i}>
                      {i + 1}. {ex.name || 'Untitled'}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {decodedQrHint.length > 0 && (
              <p className="ec-qr-hint">
                QR link{decodedQrHint.length > 1 ? 's' : ''} detected:{' '}
                {decodedQrHint.slice(0, 3).map(u => (
                  <code key={u}>{u}</code>
                ))}
                {decodedQrHint.length > 3 ? '…' : null}
              </p>
            )}
          </section>
        )}

        <section className="creator-section">
          <h2>Basic Info</h2>
          <div className="form-grid">
            <div className="form-group full-width">
              <label>Exercise Name *</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Barbell Back Squat" />
            </div>
            <div className="form-group full-width">
              <label>Description *</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe this exercise..." rows={3} />
            </div>
            <div className="form-group">
              <label>Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)}>
                {CATEGORIES.map(c => (
                  <option key={c} value={c}>
                    {c.charAt(0).toUpperCase() + c.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Difficulty</label>
              <select value={difficulty} onChange={e => setDifficulty(e.target.value)}>
                {DIFFICULTIES.map(d => (
                  <option key={d} value={d}>
                    {d.charAt(0).toUpperCase() + d.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Measurement Type</label>
              <select
                value={measurementType}
                onChange={e => {
                  const nextMeasurement = e.target.value;
                  setMeasurementType(nextMeasurement);
                  if (nextMeasurement !== 'reps') setDefaultReps('');
                  if (nextMeasurement !== 'time') setDefaultDuration('');
                }}
              >
                {MEASUREMENT_TYPES.map(m => (
                  <option key={m} value={m}>
                    {m.charAt(0).toUpperCase() + m.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Default Sets</label>
              <input type="number" min="1" max="20" value={defaultSets} onChange={e => setDefaultSets(e.target.value)} />
            </div>
            {measurementType === 'reps' && (
              <div className="form-group">
                <label>Default Reps</label>
                <input type="number" min="1" max="100" value={defaultReps} onChange={e => setDefaultReps(e.target.value)} placeholder="e.g. 10" />
              </div>
            )}
            {measurementType === 'time' && (
              <div className="form-group">
                <label>Default Duration (seconds)</label>
                <input type="number" min="1" max="3600" value={defaultDuration} onChange={e => setDefaultDuration(e.target.value)} placeholder="e.g. 30" />
              </div>
            )}
            <div className="form-group">
              <label>Rest Between Sets (seconds)</label>
              <input type="number" min="0" max="600" value={restBetweenSets} onChange={e => setRestBetweenSets(e.target.value)} />
            </div>
          </div>
        </section>

        <section className="creator-section">
          <h2>Muscles & Equipment</h2>
          <div className="form-grid">
            <div className="form-group full-width">
              <label>Target Muscles</label>
              <input type="text" value={targetMuscles} onChange={e => setTargetMuscles(e.target.value)} placeholder="quads, glutes, hamstrings" />
            </div>
            <div className="form-group full-width">
              <label>Secondary Muscles</label>
              <input type="text" value={secondaryMuscles} onChange={e => setSecondaryMuscles(e.target.value)} placeholder="core, calves" />
            </div>
            <div className="form-group full-width">
              <label>Equipment Needed</label>
              <input type="text" value={equipmentNames} onChange={e => setEquipmentNames(e.target.value)} placeholder="band, mat" />
            </div>
          </div>
        </section>

        <section className="creator-section">
          <h2>Instructions</h2>
          <div className="instructions-list">
            {instructions.map((inst, i) => (
              <div key={i} className="instruction-row">
                <span className="instruction-step">{inst.step}</span>
                <input type="text" value={inst.text} onChange={e => updateInstruction(i, e.target.value)} placeholder={`Step ${inst.step}...`} />
                {instructions.length > 1 && (
                  <button type="button" className="remove-instruction-btn" onClick={() => removeInstruction(i)}>
                    <FaTimes />
                  </button>
                )}
              </div>
            ))}
            <button type="button" className="add-instruction-btn" onClick={addInstruction}>
              <FaPlus /> Add Step
            </button>
          </div>
        </section>

        <section className="creator-section">
          <h2>Media</h2>
          <div className="form-grid">
            <div className="form-group full-width">
              <label>
                <FaYoutube style={{ color: '#ef4444', marginRight: 4 }} />
                Demo / video URL
              </label>
              <input
                type="text"
                value={video}
                onChange={e => setVideo(e.target.value)}
                placeholder="YouTube, Vimeo, https://hep.video/watch.php?id=… , etc."
              />
              {thumbUrl && (
                <div className="video-preview">
                  <img src={thumbUrl} alt="Video preview" />
                  <span className="video-preview-label">YouTube preview</span>
                </div>
              )}
            </div>
            <div className="form-group full-width">
              <label>Image URL (optional)</label>
              <input type="text" value={image} onChange={e => setImage(e.target.value)} placeholder="https://..." />
              {image && (
                <div className="image-preview">
                  <img src={image} alt="Exercise preview" />
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="creator-section">
          <h2>Tips & Safety</h2>
          <div className="form-grid">
            <div className="form-group full-width">
              <label>Tips (one per line)</label>
              <textarea value={tips} onChange={e => setTips(e.target.value)} placeholder="Keep your back straight&#10;Brace your core" rows={3} />
            </div>
            <div className="form-group full-width">
              <label>Safety Notes (one per line)</label>
              <textarea value={safetyNotes} onChange={e => setSafetyNotes(e.target.value)} placeholder="Don't round your lower back&#10;Start lighter" rows={3} />
            </div>
            <div className="form-group">
              <label>Easier Modification</label>
              <input type="text" value={modEasier} onChange={e => setModEasier(e.target.value)} placeholder="e.g. Goblet squat" />
            </div>
            <div className="form-group">
              <label>Harder Modification</label>
              <input type="text" value={modHarder} onChange={e => setModHarder(e.target.value)} placeholder="e.g. Pause squat" />
            </div>
            <div className="form-group full-width">
              <label>Tags</label>
              <input type="text" value={tags} onChange={e => setTags(e.target.value)} placeholder="pt, home-program" />
            </div>
          </div>
        </section>
      </div>

      {showPostSave && (
        <div className="post-save-overlay">
          <div className={`post-save-modal ${postSaveStep !== 'main' ? 'post-save-modal-wide' : ''}`}>
            {postSaveStep === 'main' && (
              <>
                <h2>Exercise Saved!</h2>
                <p>What would you like to do next?</p>
                <div className="post-save-actions">
                  {isCoach && (
                    <button type="button" className="post-save-btn coach" onClick={() => navigate('/coach/dashboard')}>
                      Go to Coach Dashboard
                    </button>
                  )}
                  {!isCoach && (
                    <>
                      <button type="button" className="post-save-btn workouts" onClick={() => navigate('/fitness/exercises')}>
                        Go to Exercises
                      </button>
                      {lastSavedExercise && (
                        <>
                          <button
                            type="button"
                            className="post-save-btn secondary-action"
                            disabled={placementLoading}
                            onClick={loadWorkoutsForPlacement}
                          >
                            Add to existing workout…
                          </button>
                          <button
                            type="button"
                            className="post-save-btn secondary-action"
                            disabled={placementLoading}
                            onClick={confirmCreateWorkout}
                          >
                            Create new workout with this exercise
                          </button>
                          <button
                            type="button"
                            className="post-save-btn secondary-action"
                            disabled={placementLoading}
                            onClick={loadProgramsForPlacement}
                          >
                            Assign to program day…
                          </button>
                          <button type="button" className="post-save-btn secondary-action" disabled={placementLoading} onClick={confirmCreateProgram}>
                            Create new program (1 day)
                          </button>
                        </>
                      )}
                    </>
                  )}
                  <button type="button" className="post-save-btn stay" onClick={handlePostSaveClose}>
                    Done
                  </button>
                </div>
              </>
            )}

            {postSaveStep === 'pick-workout' && (
              <>
                <h2>Add to workout</h2>
                <p>Choose a workout. The exercise will be appended to the first block.</p>
                {workoutsForPick.length === 0 ? (
                  <p className="ec-placement-empty">No saved workouts yet. Create a workout first or use &quot;Create new workout&quot;.</p>
                ) : (
                  <select
                    className="ec-placement-select"
                    value={pickWorkoutId}
                    onChange={e => setPickWorkoutId(e.target.value)}
                  >
                    {workoutsForPick.map(w => (
                      <option key={w._id} value={w._id}>
                        {w.name}
                      </option>
                    ))}
                  </select>
                )}
                <div className="post-save-actions">
                  <button type="button" className="post-save-btn workouts" disabled={placementLoading || !pickWorkoutId} onClick={confirmAddToWorkout}>
                    {placementLoading ? 'Saving…' : 'Add to workout'}
                  </button>
                  <button type="button" className="post-save-btn stay" onClick={() => { resetPlacementUi(); setPostSaveStep('main'); }}>
                    Back
                  </button>
                </div>
              </>
            )}

            {postSaveStep === 'pick-program' && (
              <>
                <h2>Assign to program</h2>
                <p>We&apos;ll create a one-exercise workout and attach it to the day you pick.</p>
                {programsForPick.length === 0 ? (
                  <p className="ec-placement-empty">No programs yet. Use &quot;Create new program&quot; from the previous step.</p>
                ) : (
                  <>
                    <label className="ec-placement-label">Program</label>
                    <select
                      className="ec-placement-select"
                      value={pickProgramId}
                      onChange={e => {
                        setPickProgramId(e.target.value);
                        setPickProgramDayIndex(0);
                      }}
                    >
                      {programsForPick.map(p => (
                        <option key={p._id} value={p._id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                    {pickProgramId && (
                      <>
                        <label className="ec-placement-label">Day slot</label>
                        <select
                          className="ec-placement-select"
                          value={pickProgramDayIndex}
                          onChange={e => setPickProgramDayIndex(parseInt(e.target.value, 10))}
                        >
                          {(programsForPick.find(p => p._id === pickProgramId)?.workouts || []).map((slot, i) => (
                            <option key={i} value={i}>
                              {slot.dayName || `Day ${slot.dayNumber}`}
                              {slot.isRestDay ? ' (rest)' : ''}
                            </option>
                          ))}
                        </select>
                      </>
                    )}
                  </>
                )}
                <div className="post-save-actions">
                  <button
                    type="button"
                    className="post-save-btn workouts"
                    disabled={placementLoading || !pickProgramId || !programsForPick.find(p => p._id === pickProgramId)?.workouts?.length}
                    onClick={confirmAssignProgramDay}
                  >
                    {placementLoading ? 'Saving…' : 'Assign to day'}
                  </button>
                  <button type="button" className="post-save-btn stay" onClick={() => { resetPlacementUi(); setPostSaveStep('main'); }}>
                    Back
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
